import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageFooter from '../components/PageFooter';
import './Admin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'http://localhost:3001/api';

interface CognitoUser {
  username: string;
  email: string;
  name: string;
  role: string;
  enabled: boolean;
  status: string;
  created: string;
  modified: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'viewer',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
    }
  }, [user?.role]);

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>Access Denied</h1>
          <p>You do not have permission to access this page.</p>
        </div>
        <PageFooter />
      </div>
    );
  }

  const loadUsers = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data);
      } else {
        setError('Failed to load users');
      }
    } catch (err: any) {
      setError(`Error loading users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!newUser.email || !newUser.name || !newUser.password) {
        setError('Please fill in all required fields');
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`User ${newUser.email} created successfully!`);
        setShowCreateModal(false);
        setNewUser({ email: '', name: '', role: 'viewer', password: '' });
        await loadUsers();
      } else {
        setError(data.message || 'Failed to create user');
      }
    } catch (err: any) {
      setError(`Error creating user: ${err.message}`);
    }
  };

  const handleUpdateRole = async (username: string, newRole: string) => {
    try {
      setError(null);
      setSuccess(null);

      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_BASE_URL}/users/${username}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Role updated for ${username}`);
        await loadUsers();
      } else {
        setError(data.message || 'Failed to update role');
      }
    } catch (err: any) {
      setError(`Error updating role: ${err.message}`);
    }
  };

  const handleDeleteUser = async (username: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${username}?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const accessToken = localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/users/${username}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`User ${username} deleted successfully`);
        await loadUsers();
      } else {
        setError(data.message || 'Failed to delete user');
      }
    } catch (err: any) {
      setError(`Error deleting user: ${err.message}`);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin': return 'role-badge-admin';
      case 'workflow_creator': return 'role-badge-creator';
      case 'workflow_executor': return 'role-badge-executor';
      case 'viewer': return 'role-badge-viewer';
      default: return 'role-badge';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'workflow_creator': return 'Creator';
      case 'workflow_executor': return 'Executor';
      case 'viewer': return 'Viewer';
      default: return role;
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>User Management</h1>
        <p>Manage users and assign roles</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="success-banner">
          <span>✅ {success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="admin-content">
        <div className="tab-content">
          <div className="content-header">
            <h2>Users</h2>
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              Create New User
            </button>
          </div>

          {loading ? (
            <div className="loading-state">Loading users...</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.username}>
                    <td>{u.email}</td>
                    <td>{u.name}</td>
                    <td>
                      <select
                        className={`role-select ${getRoleBadgeClass(u.role)}`}
                        value={u.role}
                        onChange={(e) => handleUpdateRole(u.username, e.target.value)}
                        disabled={u.email === user?.email}
                      >
                        <option value="admin">Admin</option>
                        <option value="workflow_creator">Creator</option>
                        <option value="workflow_executor">Executor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </td>
                    <td>
                      <span className={`status-badge ${u.enabled ? 'status-active' : 'status-disabled'}`}>
                        {u.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>{new Date(u.created).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleDeleteUser(u.username)}
                        disabled={u.email === user?.email}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="json-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <div className="modal-content">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </div>

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                />
                <small>Must contain uppercase, lowercase, number, and special character</small>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="workflow_executor">Executor (Can execute workflows)</option>
                  <option value="workflow_creator">Creator (Can create/edit workflows)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateUser}>
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      <PageFooter />
    </div>
  );
};

export default UserManagement;

